import { QueryKey, useMutation, UseQueryResult } from '@tanstack/react-query'
import { queryClient, QueryOptions, UseMutationResult, useResource, useResources } from '.'
import { Mission } from '../api/missions'
import {
  abandon,
  addEventsToWorkPeriods,
  addTagsToWorkPeriods,
  cancel,
  create,
  fetch,
  get,
  materializeSingleDay,
  MaterializeSingleDayPayload,
  remove,
  update,
  updateTags,
  validate,
  validateMany,
  WorkPeriodAddEventsToWorkPeriodsPayload,
  WorkPeriodAddTagsToWorkPeriodsPayload,
  WorkPeriodCreationPayload,
  WorkPeriodGetParams,
  WorkPeriodRemovePayload,
  WorkPeriodUpdatePayload,
  WorkPeriodUpdateTagsPayload,
  WorkPeriodValidateManyPayload,
  WorkPeriodValidatePayload,
  WorkPeriodWithMission,
  WorkPeriodWithMissionAndSummary,
} from '../api/work-periods'
import { invalidateMissionsQueries } from './missions'
import { useNormalizeEventsFilters } from '../hooks/use-normalize-events-filters'

//Queries

/**
 * Fetch a single work period by ID.
 * Uses GET /work-periods/:id with optional ?summary=true.
 *
 * @param id - The work period ID. Query is disabled when undefined.
 * @param params - Optional query params (e.g. { summary: true }).
 * @param options - React Query options override.
 * @returns The work period with its populated mission (and summary when requested).
 */
export const useWorkPeriodQuery = (
  id?: string,
  params?: WorkPeriodGetParams,
  options?: QueryOptions,
): UseQueryResult<WorkPeriodWithMission, any> =>
  useResource<WorkPeriodWithMission, WorkPeriodGetParams>('work-periods', get, id, params, options)

/**
 * Same as useWorkPeriodQuery but typed with summary included.
 * Automatically passes summary=true.
 *
 * @param id - The work period ID. Query is disabled when undefined.
 * @param options - React Query options override.
 */
export const useWorkPeriodWithSummaryQuery = (
  id?: string,
  options?: QueryOptions,
): UseQueryResult<WorkPeriodWithMissionAndSummary, any> =>
  useResource<WorkPeriodWithMissionAndSummary, WorkPeriodGetParams>(
    'work-periods',
    get,
    id,
    { summary: true },
    options,
  )

export const useWorkPeriodsQuery = (
  filters?: any, // TODO: type !!!
  options?: QueryOptions,
): UseQueryResult<WorkPeriodWithMission[], any> => {
  // Normalize events filters (transform sentinels to actual event IDs)
  const normalizedEvents = useNormalizeEventsFilters(filters?.events)

  // Merge normalized events into filters
  const normalizedFilters = {
    ...filters,
    ...normalizedEvents,
  }

  return useResources<WorkPeriodWithMission[], any>(
    'work-periods',
    fetch,
    normalizedFilters,
    options,
  )
}

//Mutations
const useUpdateMutationKey = (): QueryKey => ['missions', 'work-periods', 'update']
export const useUpdateMutation = (): UseMutationResult<Mission, WorkPeriodUpdatePayload, any> =>
  useMutation(update, {
    mutationKey: useUpdateMutationKey(),
    onSuccess: () => {
      invalidateWorkPeriodsQueries()
      invalidateMissionsQueries()
    },
  })

const useCreateWorkPeriodMutationKey = (): QueryKey => ['missions', 'work-periods', 'create']
export const useCreateWorkPeriodMutation = (): UseMutationResult<
  Mission,
  WorkPeriodCreationPayload,
  any
> =>
  useMutation(create, {
    mutationKey: useCreateWorkPeriodMutationKey(),
    onSuccess: () => {
      invalidateWorkPeriodsQueries()
      invalidateMissionsQueries()
    },
  })

const useValidateMutationKey = (): QueryKey => ['missions', 'work-periods', 'validate']
export const useValidateMutation = (): UseMutationResult<Mission, WorkPeriodValidatePayload, any> =>
  useMutation(validate, {
    mutationKey: useValidateMutationKey(),
    onSuccess: () => {
      invalidateWorkPeriodsQueries()
      invalidateMissionsQueries()
    },
  })

const useCancelMutationKey = (): QueryKey => ['missions', 'work-periods', 'cancel']
export const useCancelMutation = () =>
  useMutation(cancel, {
    mutationKey: useCancelMutationKey(),
    onSuccess: () => {
      invalidateWorkPeriodsQueries()
      invalidateMissionsQueries()
    },
  })

const useAbandonMutationKey = (): QueryKey => ['missions', 'work-periods', 'abandon']
export const useAbandonMutation = () =>
  useMutation(abandon, {
    mutationKey: useAbandonMutationKey(),
    onSuccess: () => {
      invalidateWorkPeriodsQueries()
      invalidateMissionsQueries()
    },
  })

const useRemoveWorkPeriodMutationKey = (): QueryKey => ['missions', 'work-periods', 'remove']
export const useRemoveWorkPeriodMutation = (): UseMutationResult<
  Mission,
  WorkPeriodRemovePayload,
  any
> =>
  useMutation(remove, {
    mutationKey: useRemoveWorkPeriodMutationKey(),
    onSuccess: () => {
      invalidateWorkPeriodsQueries()
      invalidateMissionsQueries()
    },
  })

export const useAddEventsToWorkPeriodsMutationKey = (): QueryKey => [
  'missions',
  'work-periods',
  'add-events',
]
export const useAddEventsToWorkPeriodsMutation = (): UseMutationResult<
  Mission,
  WorkPeriodAddEventsToWorkPeriodsPayload,
  any
> =>
  useMutation(addEventsToWorkPeriods, {
    mutationKey: useAddEventsToWorkPeriodsMutationKey(),
    onSuccess: () => {
      invalidateWorkPeriodsQueries()
      invalidateMissionsQueries()
    },
  })

export const useValidateManyMutationKey = (): QueryKey => [
  'missions',
  'work-periods',
  'validate-many',
]
export const useValidateManyMutation = (): UseMutationResult<
  Mission,
  WorkPeriodValidateManyPayload,
  any
> =>
  useMutation(validateMany, {
    mutationKey: useValidateManyMutationKey(),
    onSuccess: () => {
      invalidateWorkPeriodsQueries()
      invalidateMissionsQueries()
    },
  })

export const useUpdateTagsMutationKey = (): QueryKey => ['work-periods', 'update-tags']
export const useUpdateTagsMutation = (): UseMutationResult<
  WorkPeriodWithMission,
  WorkPeriodUpdateTagsPayload,
  any
> =>
  useMutation(updateTags, {
    mutationKey: useUpdateTagsMutationKey(),
    onSuccess: () => {
      invalidateWorkPeriodsQueries()
      invalidateMissionsQueries()
    },
  })

export const useAddTagsToWorkPeriodsMutationKey = (): QueryKey => [
  'missions',
  'work-periods',
  'add-tags',
]
export const useAddTagsToWorkPeriodsMutation = (): UseMutationResult<
  Mission,
  WorkPeriodAddTagsToWorkPeriodsPayload,
  any
> =>
  useMutation(addTagsToWorkPeriods, {
    mutationKey: useAddTagsToWorkPeriodsMutationKey(),
    onSuccess: () => {
      invalidateWorkPeriodsQueries()
      invalidateMissionsQueries()
    },
  })

/**
 * Mutation hook to materialize a single day's work period for a template-based mission.
 *
 * POST /api/missions/:missionId/materialize-day
 *
 * On success, invalidates both work-periods and missions queries so lists stay fresh.
 */
export const useMaterializeSingleDayMutationKey = (): QueryKey => [
  'missions',
  'work-periods',
  'materialize-day',
]
export const useMaterializeSingleDayMutation = (): UseMutationResult<
  WorkPeriodWithMission | null,
  MaterializeSingleDayPayload,
  any
> =>
  useMutation(materializeSingleDay, {
    mutationKey: useMaterializeSingleDayMutationKey(),
    onSuccess: () => {
      invalidateWorkPeriodsQueries()
      invalidateMissionsQueries()
    },
  })

export const invalidateWorkPeriodsQueries = (): void => {
  queryClient.invalidateQueries(['work-periods'])
}
